/**
 * Influencer Context
 * 
 * Manages influencer branding state across the application.
 * Persists the active influencer in localStorage so users always see
 * the branded experience after clicking an influencer link.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

const STORAGE_KEY = 'tradevision_influencer';
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface InfluencerData {
  id: string;
  slug: string;
  name: string;
  tagline?: string;
  bio?: string;
  profileImage?: string;
  bannerImage?: string;
  socialLinks?: {
    twitter?: string;
    youtube?: string;
    instagram?: string;
    tiktok?: string;
    discord?: string;
    website?: string;
  };
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    logoUrl?: string;
  };
  testimonial?: string;
  specialOffer?: {
    title?: string;
    description?: string;
    discountCode?: string;
    discountPercent?: number;
  };
  stats?: {
    followers?: string;
    experience?: string;
    trades?: string;
  };
}

interface InfluencerContextType {
  influencer: InfluencerData | null;
  isLoading: boolean;
  error: string | null;
  discountCode: string | null;
  hasFullDiscount: boolean; // True if influencer offers 100% discount (free premium access)
  setInfluencer: (influencer: InfluencerData | null) => void;
  loadInfluencerBySlug: (slug: string) => Promise<InfluencerData | null>;
  clearInfluencer: () => void;
  trackSignup: () => Promise<void>;
  getDiscountCode: () => string | null;
}

const InfluencerContext = createContext<InfluencerContextType | undefined>(undefined);

interface InfluencerProviderProps {
  children: ReactNode;
}

export const InfluencerProvider: React.FC<InfluencerProviderProps> = ({ children }) => {
  const [influencer, setInfluencerState] = useState<InfluencerData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load influencer from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setInfluencerState(parsed);
      }
    } catch (err) {
      console.error('[InfluencerContext] Failed to load influencer from storage:', err);
    }
  }, []);

  // Set influencer and persist to localStorage
  const setInfluencer = useCallback((data: InfluencerData | null) => {
    setInfluencerState(data);
    if (data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Clear influencer
  const clearInfluencer = useCallback(() => {
    setInfluencerState(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Load influencer by slug from API
  const loadInfluencerBySlug = useCallback(async (slug: string): Promise<InfluencerData | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/influencer/public/${slug}`);
      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error || 'Influencer not found');
        return null;
      }

      const data = result.data as InfluencerData;
      setInfluencer(data);
      return data;
    } catch (err: any) {
      console.error('Failed to load influencer:', err);
      setError(err.message || 'Failed to load influencer');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [setInfluencer]);

  // Track signup attributed to influencer
  const trackSignup = useCallback(async () => {
    if (!influencer) return;

    try {
      await fetch(`${API_BASE}/api/influencer/public/${influencer.slug}/track-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      console.error('Failed to track signup:', err);
    }
  }, [influencer]);

  // Get discount code from current influencer
  const getDiscountCode = useCallback((): string | null => {
    return influencer?.specialOffer?.discountCode || null;
  }, [influencer]);

  // Computed discount code value
  const discountCode = influencer?.specialOffer?.discountCode || null;
  
  // Check if influencer offers 100% discount (free premium access)
  const hasFullDiscount = influencer?.specialOffer?.discountPercent === 100;

  return (
    <InfluencerContext.Provider
      value={{
        influencer,
        isLoading,
        error,
        discountCode,
        hasFullDiscount,
        setInfluencer,
        loadInfluencerBySlug,
        clearInfluencer,
        trackSignup,
        getDiscountCode,
      }}
    >
      {children}
    </InfluencerContext.Provider>
  );
};

export const useInfluencer = (): InfluencerContextType => {
  const context = useContext(InfluencerContext);
  if (!context) {
    throw new Error('useInfluencer must be used within an InfluencerProvider');
  }
  return context;
};

export default InfluencerContext;
